import React, { createContext, useContext, useMemo } from 'react';
import { DEFAULT_PALETTE_ID, getPalette, paletteToCssVars } from './palettes';
import {
    DEFAULT_TEMPLATE_ID,
    getTemplate,
    SECTION_DEFAULTS,
    LOCKED_SECTIONS
} from './templates';

const TenantThemeContext = createContext(null);

/**
 * TenantThemeProvider
 * Wraps the public tenant pages and provides:
 *   - tenant data (the API payload)
 *   - resolved template + palette
 *   - effective enabledSections (defaults + auto-on rules + owner overrides)
 *   - css vars for the public site (applied on a [data-tenant-theme] root)
 */
export function TenantThemeProvider({ tenant, children }) {
    const value = useMemo(() => {
        const templateId = tenant?.template_style || DEFAULT_TEMPLATE_ID;
        const paletteId = tenant?.palette_id || DEFAULT_PALETTE_ID;
        const accentSwatch = tenant?.accent_swatch || null;
        const overrides = tenant?.enabled_sections || {};

        const template = getTemplate(templateId);
        const palette = getPalette(paletteId);
        const cssVars = paletteToCssVars(palette, accentSwatch);

        // Auto-on rules — sections "wake up" when relevant content exists.
        const counts = {
            specializations: (tenant?.specializations?.length || 0)
                + (tenant?.certifications?.length || 0),
            before_after: tenant?.before_after_gallery?.length || 0,
            real_work_reel: tenant?.featured_videos?.length || 0,
            team: tenant?.team_members?.length || 0,
            blog_teaser: tenant?.blog_count || 0
        };
        const conveniencePromises = tenant?.convenience_promises || {};
        const conveniencePromiseEnabled = Object.values(conveniencePromises)
            .some(v => v && typeof v === 'object' && v.enabled);

        const auto = {
            specializations: counts.specializations >= 1,
            before_after: counts.before_after >= 3,
            real_work_reel: counts.real_work_reel >= 1,
            team: counts.team >= 1,
            blog_teaser: counts.blog_teaser >= 1,
            convenience: conveniencePromiseEnabled
        };

        const enabledSections = {};
        Object.entries(SECTION_DEFAULTS).forEach(([key, def]) => {
            if (LOCKED_SECTIONS.has(key)) {
                enabledSections[key] = true;
                return;
            }
            const ownerOverride = overrides[key];
            if (typeof ownerOverride === 'boolean') {
                enabledSections[key] = ownerOverride;
                return;
            }
            // No explicit override → auto-on rule wins, else default.
            enabledSections[key] = auto[key] !== undefined ? auto[key] : def;
        });

        return {
            tenant,
            template,
            templateId,
            palette,
            paletteId,
            accentSwatch,
            cssVars,
            enabledSections,
            sectionEnabled: (key) => !!enabledSections[key]
        };
    }, [tenant]);

    return (
        <TenantThemeContext.Provider value={value}>
            <div
                data-tenant-theme={value.paletteId}
                data-template={value.templateId}
                style={value.cssVars}
                className="tenant-theme-root"
            >
                <style>{`
                    @keyframes tickerScroll {
                        from { transform: translateX(0); }
                        to   { transform: translateX(-50%); }
                    }
                    /* Force Bebas Neue across all tenant landing typography
                       (display + headline) so heading consistency is preserved
                       regardless of which template/section utility classes are
                       used. Scoped via classes rather than tag names so Radix
                       primitives that wrap their triggers in <h3>/<h4>
                       (Accordion, Dialog, etc.) keep readable body type. */
                    .tenant-theme-root .font-headline,
                    .tenant-theme-root .font-display {
                        font-family: "Bebas Neue", Impact, system-ui, sans-serif !important;
                        letter-spacing: 0.01em;
                    }
                    /* Bare h1/h2 still use Bebas, but only when no font-* class
                       has overridden them (e.g. inside Accordion/Dialog). */
                    .tenant-theme-root h1:not([class*="font-"]),
                    .tenant-theme-root h2:not([class*="font-"]) {
                        font-family: "Bebas Neue", Impact, system-ui, sans-serif !important;
                        letter-spacing: 0.01em;
                    }
                    /* Bengali subtitles & body should keep the Bengali stack. */
                    .tenant-theme-root .font-bn,
                    .tenant-theme-root [lang="bn"] {
                        font-family: "Noto Sans Bengali", "DM Sans", system-ui, sans-serif !important;
                        letter-spacing: normal;
                    }
                    /* Heritage – soft beige surface tint + ornament typography */
                    .tenant-theme-root[data-template="heritage"] {
                        --tenant-surface-alt: color-mix(in srgb, var(--tenant-surface) 92%, var(--tenant-primary) 8%);
                    }
                    .tenant-theme-root[data-template="heritage"] h2,
                    .tenant-theme-root[data-template="heritage"] h3 {
                        letter-spacing: -0.01em;
                    }
                    /* Workshop – tighter, punchier headings */
                    .tenant-theme-root[data-template="workshop"] section {
                        --section-rule: 3px;
                    }
                    /* Showroom – airy spacing on body copy */
                    .tenant-theme-root[data-template="showroom"] p {
                        letter-spacing: 0.005em;
                    }
                    /* Express – tighten section gaps */
                    .tenant-theme-root[data-template="express"] section + section {
                        margin-top: 0;
                    }
                    /* Accessibility — keyboard focus rings tinted by the
                       active tenant primary. Mouse interactions stay clean
                       (focus-visible, not :focus). */
                    .tenant-theme-root :focus-visible {
                        outline: 2px solid var(--tenant-primary);
                        outline-offset: 2px;
                        border-radius: 4px;
                    }
                    .tenant-theme-root a:focus-visible,
                    .tenant-theme-root button:focus-visible {
                        outline-offset: 3px;
                    }
                    /* Honor reduced-motion globally inside the tenant tree.
                       Components that already gate on useReducedMotion are
                       unaffected; this kills any leftover CSS animations. */
                    @media (prefers-reduced-motion: reduce) {
                        .tenant-theme-root *,
                        .tenant-theme-root *::before,
                        .tenant-theme-root *::after {
                            animation-duration: 0.001ms !important;
                            animation-iteration-count: 1 !important;
                            transition-duration: 0.001ms !important;
                            scroll-behavior: auto !important;
                        }
                    }
                `}</style>
                {children}
            </div>
        </TenantThemeContext.Provider>
    );
}

export function useTenantTheme() {
    const ctx = useContext(TenantThemeContext);
    if (!ctx) {
        throw new Error('useTenantTheme must be used inside <TenantThemeProvider>');
    }
    return ctx;
}
